//@flow
import Model from '../../Utils/Model/model';
import _ from 'underscore';

import type QuoteItemsCollection from '../Quote/quoteItemsCollection';
import type { OrderItemObject } from '../../Stores/Order/orderItemModel';

export type CategoryObject = {
  id:string,
  name:string,
  child_categories:string[],
  product_skus:string[],
  url_key:string,
  thumbnail_image:?string,
  image:?string,
  meta_title?:string,
  meta_description?:string,
};

const notAllowedCategories = [
  'm-meals',
  'm-variety-packs',
  'm-beverages',
  'm-squeeze-packs',
  'm-protein-shakes',
  'm-pantry-staples',
  'm-last-chance',
];

export default class CategoryModel extends Model<CategoryObject> {
  getId():string {
    return this.attributes.id;
  }

  getName():string {
    return this.attributes.name || '';
  }

  getUrlKey():string {
    return this.attributes.url_key || '';
  }

  getUrl():string {
    return '/products/' + this.getUrlKey();
  }

  getOfficeUrl():string {
    return '/office/catalog/products/' + this.getUrlKey();
  }

  getImage():?string {
    return this.attributes.image;
  }

  getMetaTitle():string {
    return this.attributes.meta_title || '';
  }

  getMetaDescription():string {
    return this.attributes.meta_description || '';
  }

  getThumbnailImageUrl():string {
    const imagePath = this.getImage();
    if (!imagePath) return '';
    return `${this._getResizeUrl(296, 214)}/category${imagePath}`;
  }

  getThumbnailSliderImageUrl():string {
    const imagePath = this.attributes.thumbnail_image;
    if (!imagePath) return '';
    return `${this._getResizeUrl(176, 176)}/category${imagePath}`;
  }

  getShowcaseFooterText():string {
    return this.getName();
  }

  getRandomProductSkus(excludeSku:string, count:number):string[] {
    const productSkus = this.getProductsSkus();
    if (!productSkus) return [];

    return _.shuffle(_.without(productSkus, excludeSku)).slice(0, count);
  }

  showCategoryThumbnail():boolean {
    return !notAllowedCategories.includes(this.getUrlKey());
  }

  getQuoteItemSkus(quoteItems:?QuoteItemsCollection):string[] {
    const itemSkus = quoteItems && quoteItems.getQuoteItemSkus();
    if (!itemSkus) return [];

    const skus = this.getProductsSkus();
    return _.intersection(skus, itemSkus);
  }

  hasQuoteItem(quoteItems:?QuoteItemsCollection):boolean {
    return this.getQuoteItemSkus(quoteItems).length > 0;
  }

  getItemSkus(orderItems:Object[]):string[] {
    const itemSkus = orderItems && orderItems.map(item => item.sku);
    if (!itemSkus) return [];

    const skus = this.getProductsSkus();
    return _.intersection(skus, itemSkus);
  }

  hasOrderItem(orderItems:OrderItemObject[]):boolean {
    return this.getItemSkus(orderItems).length > 0;
  }

  hasSku(sku:?string):boolean {
    if (!sku) return false;
    const skus = this.getProductsSkus();
    if (!skus) return false;
    return skus.indexOf(sku) > -1;
  }

  getProductsSkus():string[] {
    return this.attributes.product_skus || [];
  }
}
